import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, PersonAdd, Save, SettingsInputComponent } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import type { TransferGroup, TransferGroupStrategy, UserWithGroups } from '../types';
import DetailHeader from '../components/DetailHeader';
import AgentSelectedMemberEditor from '../components/transferGroups/AgentSelectedMemberEditor';
import {
  externalMemberRow,
  firstNestedMessage,
  memberFormRow,
  memberPayload,
  userMemberRow,
  type TransferGroupMemberFormRow,
} from '../components/transferGroups/memberForm';
import { usePermissions } from '../contexts/PermissionContext';
import usePageTitle from '../hooks/usePageTitle';

interface TransferGroupFormState {
  name: string;
  strategy: TransferGroupStrategy;
  isActive: boolean;
  externalExtensionDialPrefix: string;
  transferAnnouncementAudioId: string;
  members: TransferGroupMemberFormRow[];
}

interface AnnouncementAudioOption {
  id: string;
  displayName: string;
  originalFilename: string;
}

const INITIAL_FORM_STATE: TransferGroupFormState = {
  name: '',
  strategy: 'rrmemory',
  isActive: true,
  externalExtensionDialPrefix: '',
  transferAnnouncementAudioId: '',
  members: [],
};

const QUEUE_ENDPOINT_TYPES = new Set(['internal_extension', 'external_extension']);

export default function TransferGroupFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isEditing = Boolean(id);
  const canRead = hasPermission('transfer_groups_read');
  const canWrite = hasPermission(isEditing ? 'transfer_groups_update' : 'transfer_groups_create');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<TransferGroupFormState>(INITIAL_FORM_STATE);
  const [eligibleUsers, setEligibleUsers] = useState<UserWithGroups[]>([]);
  const [announcementAudios, setAnnouncementAudios] = useState<AnnouncementAudioOption[]>([]);

  usePageTitle(
    isEditing
      ? t('pages:transferGroups.form.editTitle')
      : t('pages:transferGroups.form.newTitle'),
  );

  const strategyOptions = useMemo(
    () => ([
      { value: 'rrmemory' as const, label: t('pages:transferGroups.strategyOptions.rrmemory') },
      { value: 'leastrecent' as const, label: t('pages:transferGroups.strategyOptions.leastrecent') },
      { value: 'fewestcalls' as const, label: t('pages:transferGroups.strategyOptions.fewestcalls') },
      { value: 'ringall' as const, label: t('pages:transferGroups.strategyOptions.ringall') },
      { value: 'agent_selected' as const, label: t('pages:transferGroups.strategyOptions.agent_selected') },
    ]),
    [t],
  );

  const loadEligibleUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/users/', {
        params: { page: 1, page_size: 500, ordering: 'email' },
      });
      const rawUsers: UserWithGroups[] = response.data.results ?? response.data ?? [];
      setEligibleUsers(rawUsers.filter((user) => Boolean(user.telephonyEndpoint?.isActive)));
    } catch (requestError: unknown) {
      console.error('Error loading eligible users:', requestError);
      setEligibleUsers([]);
    }
  }, []);

  const loadAnnouncementAudios = useCallback(async () => {
    try {
      const response = await api.get('/api/media/', {
        params: { file_type: 'reverse_ivr_audio', status: 'completed', page_size: 200 },
      });
      setAnnouncementAudios(response.data.results ?? response.data ?? []);
    } catch (requestError: unknown) {
      console.error('Error loading announcement audios:', requestError);
      setAnnouncementAudios([]);
    }
  }, []);

  const loadGroup = useCallback(async () => {
    if (!id) {
      setForm(INITIAL_FORM_STATE);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get<TransferGroup>(`/api/transfer-groups/${id}/`);
      const payload = response.data;
      setForm({
        name: payload.name,
        strategy: payload.strategy,
        isActive: payload.isActive,
        externalExtensionDialPrefix: payload.externalExtensionDialPrefix ?? '',
        transferAnnouncementAudioId: payload.transferAnnouncementAudio?.id ?? '',
        members: (payload.members ?? []).map(memberFormRow),
      });
    } catch (requestError: unknown) {
      console.error('Error loading transfer group:', requestError);
      setError(t('pages:transferGroups.errors.loadDetail'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    loadEligibleUsers();
    loadAnnouncementAudios();
    loadGroup();
  }, [canRead, loadEligibleUsers, loadAnnouncementAudios, loadGroup]);

  const agentSelected = form.strategy === 'agent_selected';

  const strategyUsers = useMemo(
    () => eligibleUsers.filter((user) => (
      agentSelected || QUEUE_ENDPOINT_TYPES.has(String(user.telephonyEndpoint?.endpointType ?? ''))
    )),
    [agentSelected, eligibleUsers],
  );

  const selectedUserIds = useMemo(
    () => new Set(form.members.map((member) => member.userId).filter(Boolean)),
    [form.members],
  );

  const availableUsers = useMemo(
    () => strategyUsers.filter((user) => !selectedUserIds.has(user.id)),
    [strategyUsers, selectedUserIds],
  );

  const hasExternalMembers = form.members.some((member) => member.kind === 'external');

  const updateMember = (index: number, partial: Partial<TransferGroupMemberFormRow>) => {
    setForm((prev) => ({
      ...prev,
      members: prev.members.map((row, rowIndex) => (rowIndex === index ? { ...row, ...partial } : row)),
    }));
  };

  const removeMember = (index: number) => {
    setForm((prev) => ({
      ...prev,
      members: prev.members.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const addUserMember = (user: UserWithGroups) => {
    setForm((prev) => ({ ...prev, members: [...prev.members, userMemberRow(user)] }));
  };

  const validateAgentSelectedMembers = (): string | null => {
    const keys = form.members.map((member) => member.selectionKey.trim());
    if (keys.some((key) => !key)) {
      return t('pages:transferGroups.errors.keyRequired');
    }
    if (new Set(keys.map((key) => key.toLowerCase())).size !== keys.length) {
      return t('pages:transferGroups.errors.keyDuplicate');
    }
    for (const member of form.members) {
      if (member.kind !== 'external') {
        continue;
      }
      if (!member.displayName.trim()) {
        return t('pages:transferGroups.errors.externalNameRequired');
      }
      if (member.endpoints.length === 0 || member.endpoints.some((endpoint) => !endpoint.value.trim())) {
        return t('pages:transferGroups.errors.externalEndpointRequired');
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!canWrite) {
      return;
    }
    setError(null);
    setSuccess(null);
    if (!agentSelected && hasExternalMembers) {
      setError(t('pages:transferGroups.errors.externalRequiresAgentSelected'));
      return;
    }
    const validationError = agentSelected ? validateAgentSelectedMembers() : null;
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        strategy: form.strategy,
        isActive: form.isActive,
        externalExtensionDialPrefix: form.externalExtensionDialPrefix.trim(),
        transferAnnouncementAudioId: form.transferAnnouncementAudioId || null,
        members: form.members.map((member) => memberPayload(member, agentSelected)),
      };

      if (isEditing) {
        const response = await api.patch<TransferGroup>(`/api/transfer-groups/${id}/`, payload);
        setForm((prev) => ({ ...prev, members: (response.data.members ?? []).map(memberFormRow) }));
        setSuccess(t('pages:transferGroups.form.saveSuccess'));
      } else {
        await api.post('/api/transfer-groups/', payload);
        navigate('/transfer-groups');
      }
    } catch (requestError: unknown) {
      console.error('Error saving transfer group:', requestError);
      const responseData = (requestError as { response?: { data?: unknown } })?.response?.data;
      setError(firstNestedMessage(responseData) ?? t('pages:transferGroups.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return <Alert severity="warning">{t('pages:transferGroups.accessDenied')}</Alert>;
  }

  const breadcrumbs = [
    { label: t('common:menu.transferGroups'), to: '/transfer-groups' },
    { label: isEditing ? t('common:actions.edit') : t('common:actions.create') },
  ];

  const userPicker = (
    <Autocomplete
      options={availableUsers}
      getOptionLabel={(option) => option.email}
      value={null}
      disabled={!canWrite}
      onChange={(_, selected) => {
        if (selected) {
          addUserMember(selected);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(agentSelected ? 'pages:transferGroups.form.addUserMember' : 'pages:transferGroups.form.addMember')}
        />
      )}
    />
  );

  return (
    <Box>
      <DetailHeader
        title={isEditing ? t('pages:transferGroups.form.editTitle') : t('pages:transferGroups.form.newTitle')}
        sectionIcon={<SettingsInputComponent sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={id ?? null}
        breadcrumbs={breadcrumbs}
        backHref="/transfer-groups"
        actions={(
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={saving || !canWrite || !form.name.trim()}
          >
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          {loading ? (
            <Typography color="text.secondary">{t('common:common.loading')}</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr 1fr' } }}>
                <TextField
                  label={t('pages:transferGroups.form.name')}
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                  required
                  disabled={!canWrite}
                />
                <FormControl fullWidth disabled={!canWrite}>
                  <InputLabel>{t('pages:transferGroups.form.strategy')}</InputLabel>
                  <Select
                    value={form.strategy}
                    label={t('pages:transferGroups.form.strategy')}
                    onChange={(event) => {
                      const strategy = event.target.value as TransferGroupStrategy;
                      setForm((prev) => ({ ...prev, strategy }));
                    }}
                  >
                    {strategyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  {agentSelected && (
                    <FormHelperText>{t('pages:transferGroups.form.agentSelectedHelper')}</FormHelperText>
                  )}
                </FormControl>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.isActive}
                      onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                      disabled={!canWrite}
                    />
                  )}
                  label={t('pages:transferGroups.form.active')}
                />
              </Box>

              {agentSelected && (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                  <TextField
                    label={t('pages:transferGroups.form.dialPrefix')}
                    helperText={t('pages:transferGroups.form.dialPrefixHelper')}
                    placeholder="+55113777"
                    value={form.externalExtensionDialPrefix}
                    onChange={(event) => setForm((prev) => ({ ...prev, externalExtensionDialPrefix: event.target.value }))}
                    disabled={!canWrite}
                  />
                  <FormControl disabled={!canWrite}>
                    <InputLabel>{t('pages:transferGroups.form.announcementAudio')}</InputLabel>
                    <Select
                      value={form.transferAnnouncementAudioId}
                      label={t('pages:transferGroups.form.announcementAudio')}
                      onChange={(event) => setForm((prev) => ({ ...prev, transferAnnouncementAudioId: String(event.target.value) }))}
                    >
                      <MenuItem value="">{t('pages:transferGroups.form.noAnnouncementAudio')}</MenuItem>
                      {announcementAudios.map((audio) => (
                        <MenuItem key={audio.id} value={audio.id}>{audio.displayName || audio.originalFilename}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{t('pages:transferGroups.form.announcementAudioHelper')}</FormHelperText>
                  </FormControl>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t('pages:transferGroups.form.members')}
                </Typography>
                {agentSelected ? (
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '2fr auto' }, alignItems: 'center' }}>
                    {userPicker}
                    <Button
                      variant="outlined"
                      startIcon={<PersonAdd />}
                      disabled={!canWrite}
                      onClick={() => setForm((prev) => ({ ...prev, members: [...prev.members, externalMemberRow()] }))}
                    >
                      {t('pages:transferGroups.form.addExternalMember')}
                    </Button>
                  </Box>
                ) : userPicker}
              </Box>

              {!agentSelected && hasExternalMembers && (
                <Alert severity="warning">{t('pages:transferGroups.errors.externalRequiresAgentSelected')}</Alert>
              )}

              {form.members.length === 0 && (
                <Box>
                  <Alert severity="info">{t('pages:transferGroups.form.noMembers')}</Alert>
                </Box>
              )}

              {agentSelected ? form.members.map((member, index) => (
                <AgentSelectedMemberEditor
                  key={member.rowKey}
                  member={member}
                  fallbackKeys={form.members
                    .filter((other) => other.rowKey !== member.rowKey)
                    .map((other) => other.selectionKey.trim())
                    .filter(Boolean)}
                  disabled={!canWrite}
                  onChange={(partial) => updateMember(index, partial)}
                  onRemove={() => removeMember(index)}
                />
              )) : form.members.map((member, index) => (
                <Box key={member.rowKey}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' },
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Chip
                      label={member.kind === 'external' ? member.displayName : (member.userLabel || member.userId)}
                      color={member.kind === 'external' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={member.isActive}
                          onChange={(event) => updateMember(index, { isActive: event.target.checked })}
                          disabled={!canWrite}
                        />
                      )}
                      label={t('common:status.active')}
                    />
                    <IconButton color="error" disabled={!canWrite} onClick={() => removeMember(index)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {canWrite && !agentSelected && (
                <Box>
                  <Button
                    startIcon={<Add />}
                    variant="text"
                    onClick={() => {
                      if (availableUsers.length > 0) {
                        addUserMember(availableUsers[0]);
                      }
                    }}
                    disabled={availableUsers.length === 0}
                  >
                    {t('pages:transferGroups.form.addFirstAvailable')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
